import { useCallback, useEffect, useMemo, useState } from 'react';

import { HiDotsVertical } from 'react-icons/hi';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Modal } from '~/components';
import { useMuscleGroup } from '~/hooks/muscleGroup';
import api from '~/services/api';

import { DivisionDataResults } from '../../Basic/TrainingProtocol/PrescriptionScreen/types';

import {
  Container,
  Fieldset,
  Text,
  LineWrapper,
  ModalButton,
  ModalButtonsContainer,
  Options,
  Section,
  Title,
  ModalContainer,
  MuscleGroupContainer,
  CheckButton,
} from './styles';

type ExEditorExercises = {
  name: string;
  id: number;
};

type ExEditor = {
  muscle_segment: string;
  exercicies: ExEditorExercises[];
};

type CardProps = {
  program: DivisionDataResults;
  studentId: string;
  billingId: string;
  trainingProgramId: number | undefined;
  updateDivisions: () => Promise<void>;
};

export const Card = ({
  program,
  studentId,
  billingId,
  trainingProgramId,
  updateDivisions,
}: CardProps) => {
  const { push } = useHistory();
  const { updateGroup } = useMuscleGroup();

  const sheetUrl = useMemo(() => {
    return `${process.env.REACT_APP_API_URL}/students/${studentId}/sheet/${trainingProgramId}/pdf?division=${program.division}`;
  }, [program, studentId, trainingProgramId]);

  const [modalIsActive, setModalIsActive] = useState(false);
  const [showMuscleGroupModal, setShowMuscleGroupModal] = useState(false);

  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<string[]>(
    [],
  );
  const [checkMuscleGroupOptions, setCheckMuscleGroupOptions] = useState<
    string[]
  >([]);
  const [defaultSelectedGroups, setDefaultSelectedGroups] = useState<string[]>(
    [],
  );

  const handleCheckboxSelection = useCallback(
    (id: string) => {
      selectedMuscleGroups.includes(id)
        ? setSelectedMuscleGroups(
            selectedMuscleGroups.filter((item) => item !== id),
          )
        : setSelectedMuscleGroups([...selectedMuscleGroups, id]);
    },
    [selectedMuscleGroups],
  );

  const CheckMuscleGroup = useMemo(
    () =>
      checkMuscleGroupOptions.map((item) => (
        <CheckButton
          key={item}
          isSelected={selectedMuscleGroups.includes(item)}
          onClick={() => handleCheckboxSelection(item)}
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item}</p>
        </CheckButton>
      )),
    [checkMuscleGroupOptions, selectedMuscleGroups, handleCheckboxSelection],
  );

  const handleDeleteDivision = async () => {
    try {
      await api.delete(
        `/training-programs/${program.training_program_id}/divisions/${program.id}`,
      );

      push(`/professional/students/${studentId}`, {
        component: 'trainingPrograms',
      });
    } catch (error) {
      console.log('error:', error);
    }
  };

  const handleNavigateToDivisionDetails = () => {
    if (!program.muscle_group) {
      toast.error('Essa divisão ainda não possui um segmento selecionado!');
      toast.warn('Selecione um segumento em "Editar".');
      return;
    }

    updateGroup(defaultSelectedGroups);

    push(
      `/professional/students/${studentId}/billing/${billingId}/training-programs/${program.id}/premium/training-division`,
      {
        division: program,
        programId: program.training_program_id,
      },
    );
  };

  const handleSubmit = useCallback(async () => {
    const formattedGroups = selectedMuscleGroups.join(',');

    if (formattedGroups.length > 1) {
      try {
        await api.put(
          `/training-programs/${program.training_program_id}/divisions/${program.id}`,
          {
            muscle_group: formattedGroups,
          },
        );

        setDefaultSelectedGroups(selectedMuscleGroups);
        await updateDivisions();
        handleCloseMuscleGroupModal();
      } catch (err) {
        toast.error(`Ocorreu um erro com a sua solicitação!`);
      }
    } else {
      setSelectedMuscleGroups(defaultSelectedGroups);
      toast.error('Selecione ao menos 1 grupo muscular');
    }
  }, [selectedMuscleGroups, program, defaultSelectedGroups]);

  const handleOpenModal = () => setModalIsActive(true);
  const handleCloseModal = () => setModalIsActive(false);
  const handleOpenMuscleGroupModal = () => setShowMuscleGroupModal(true);
  const handleCloseMuscleGroupModal = () => setShowMuscleGroupModal(false);

  const ModalView = () => (
    <Modal isOpen={modalIsActive} setOpen={setModalIsActive}>
      <Fieldset>
        <Title>ATENÇÃO</Title>
        <Text>
          Esta função excluíra todas as divisões de treino, e você terá que
          reprograma-las novamente. Tem certeza que deseja continuar?
        </Text>
      </Fieldset>

      <ModalButtonsContainer>
        <ModalButton onClick={handleDeleteDivision}>Sim</ModalButton>
        <ModalButton onClick={handleCloseModal}>Não</ModalButton>
      </ModalButtonsContainer>
    </Modal>
  );

  function MuscleGroupModal() {
    return (
      <Modal
        isOpen={showMuscleGroupModal}
        setOpen={setShowMuscleGroupModal}
        maxHeight="70%"
      >
        <ModalContainer>
          <h1>GRUPO MUSCULAR</h1>
          <MuscleGroupContainer>{CheckMuscleGroup}</MuscleGroupContainer>
          <ModalButton onClick={handleSubmit}>Salvar</ModalButton>
        </ModalContainer>
      </Modal>
    );
  }

  // Carrega a lista de grupos muscular
  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get<ExEditor[]>(`ex-editors`);

      const mappedMuscleGroups = data.map((group) => group.muscle_segment);

      setCheckMuscleGroupOptions(mappedMuscleGroups);
    }

    fetchData();
  }, [program]);

  // Carrega a lista de grupos selecionados anteriormente
  useEffect(() => {
    const mappedGroups = program.muscle_group;

    const groups = mappedGroups ? mappedGroups.split(`,`) : [];
    setDefaultSelectedGroups(groups);
    setSelectedMuscleGroups(groups);
  }, [program]);

  useEffect(() => {
    updateGroup(defaultSelectedGroups);
  }, [defaultSelectedGroups]);

  return (
    <Container active={program.controlPanel.status}>
      <Options>
        <ModalView />

        <MuscleGroupModal />

        <HiDotsVertical size={22} color="#000" />

        <div>
          {program?.controlPanel?.status && (
            <>
              <button type="button">
                <a href={sheetUrl} rel="noreferrer" target="_blank">
                  Ficha de treino
                </a>
              </button>

              <div className="divider" />
            </>
          )}

          <button onClick={handleOpenMuscleGroupModal} type="button">
            Editar
          </button>

          <div className="divider" />

          <button onClick={() => setModalIsActive(true)} type="button">
            Duplicar
          </button>

          <div className="divider" />

          <button onClick={handleOpenModal} type="button">
            Excluir
          </button>
        </div>
      </Options>

      <Section onClick={handleNavigateToDivisionDetails}>
        <LineWrapper>
          <p>Divisão de treino:</p> &nbsp;
          <q>
            <strong>{program.division}</strong>
          </q>
        </LineWrapper>

        <LineWrapper>
          <p>Segmento:</p> &nbsp;
          <strong>
            {program?.muscle_group?.replaceAll(',', '&') ||
              'Nenhum selecionado'}
          </strong>
        </LineWrapper>
      </Section>
    </Container>
  );
};
